import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3,
  Clock,
  Download,
  Layers,
  TrendingUp,
  Users,
} from "lucide-react";
import { adminRetention, adminRetentionCsvUrl } from "../lib/api";

// Compact stat cell used in the summary row.
const SummaryCell = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="ae-card p-5 flex flex-col gap-1.5"
  >
    <div className="flex items-center gap-2">
      <div className="h-9 w-9 rounded-lg bg-[#96ded1]/25 border border-[#96ded1]/60 grid place-items-center">
        <Icon className="h-4 w-4 text-[#1c2d3d]" strokeWidth={2.2} />
      </div>
      <span className="font-bold uppercase text-[10px] tracking-[0.18em] text-[#4a6984]/75">
        {label}
      </span>
    </div>
    <p className="font-black text-2xl text-[#1c2d3d] tabular-nums leading-none mt-1">
      {value}
    </p>
    {sub && (
      <p className="text-[11px] font-semibold text-[#4a6984]/75 leading-snug">
        {sub}
      </p>
    )}
  </div>
);

const featureLabels = {
  lesson_generator: "Lesson Generator",
  reading_generator: "Reading Generator",
  unit_plan_generator: "Unit Plan Generator",
  report_card_builder: "Report Card Builder (per-subject)",
  live_reports: "Live Reports",
  planner: "Planner",
  refine_chat: "Refine Chat",
  export_any: "Any Export (PDF / DOCX / PPTX)",
};

const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;

// Tiny sortable table — kept inline so we don't grow the component
// directory for one founder-facing surface.
const SortableTable = ({ columns, rows, testid }) => {
  const [sortKey, setSortKey] = useState(columns[0]?.key);
  const [dir, setDir] = useState("desc");

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === bv) return 0;
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, dir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setDir("desc");
    }
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-[#4a6984]/10">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-[#f4f5f7]">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                onClick={() => handleSort(c.key)}
                className="text-left font-extrabold uppercase tracking-[0.12em] text-xs text-[#4a6984] p-3 border-b border-[#4a6984]/15 cursor-pointer select-none hover:bg-[#4a6984]/5"
                data-testid={`${testid}-sort-${c.key}`}
              >
                {c.label}
                {sortKey === c.key && (
                  <span className="ml-1 text-[#1c2d3d]">
                    {dir === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={i} className="hover:bg-[#f4f5f7]/60">
              {columns.map((c) => (
                <td
                  key={c.key}
                  className="p-3 text-[#4a6984] border-b border-[#4a6984]/10 tabular-nums"
                >
                  {c.render ? c.render(row[c.key], row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const RetentionUsagePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(0);

  useEffect(() => {
    let abort = false;
    (async () => {
      setLoading(true);
      try {
        const d = await adminRetention(windowDays);
        if (!abort) setData(d);
      } catch (err) {
        if (!abort) {
          toast.error("Could not load retention data", {
            description: err?.response?.data?.detail || err?.message,
          });
        }
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [windowDays]);

  if (loading) {
    return (
      <p className="text-[#4a6984]/70" data-testid="retention-loading">
        Loading retention data…
      </p>
    );
  }
  if (!data) return null;

  const featureRows = Object.entries(data.feature_penetration || {}).map(
    ([key, v]) => ({
      feature: featureLabels[key] || key,
      used: v.used,
      total: v.total_paid,
      pct: v.pct,
    }),
  );

  const cohortRows = [
    {
      cohort: "Retained (W1 + W2)",
      count: data.cohort_w1_w2?.retained || 0,
      hint: "Generated lessons in both their first and second week.",
    },
    {
      cohort: "At Risk (W1 only)",
      count: data.cohort_w1_w2?.at_risk || 0,
      hint: "Generated in week 1 but nothing in week 2.",
    },
    {
      cohort: "Churned (neither)",
      count: data.cohort_w1_w2?.churned || 0,
      hint: "No lessons generated in week 1 or week 2.",
    },
  ];

  const histogram = data.depth_last_7_days?.histogram || {};

  return (
    <div className="space-y-8" data-testid="retention-panel">
      {/* Header + filters + CSV export */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-extrabold text-xl text-[#4a6984] tracking-tight">
            Retention & Usage
          </h2>
          <p className="text-sm text-[#4a6984]/75 mt-1">
            Trial conversion, week-over-week retention, depth of use, and
            feature penetration across signed-up teachers.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="inline-flex rounded-md border border-[#4a6984]/20 bg-white p-0.5"
            data-testid="retention-window-toggle"
          >
            {[
              { label: "7d", value: 7 },
              { label: "30d", value: 30 },
              { label: "All", value: 0 },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setWindowDays(opt.value)}
                data-testid={`retention-window-${opt.label.toLowerCase()}`}
                className={`px-3 py-1.5 text-xs font-extrabold uppercase tracking-wider rounded ${
                  windowDays === opt.value
                    ? "bg-[#1c2d3d] text-white"
                    : "text-[#4a6984] hover:bg-[#f4f5f7]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <a
            href={adminRetentionCsvUrl()}
            data-testid="retention-csv"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#4a6984]/20 bg-white text-[#4a6984] font-semibold text-sm hover:bg-[#f4f5f7] hover:border-[#4a6984]/35 transition-colors"
          >
            <Download className="h-3.5 w-3.5" /> Anonymous CSV
          </a>
        </div>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCell
          icon={TrendingUp}
          label="Trial conversion"
          value={fmtPct(data.trial_conversion?.conversion_rate_pct)}
          sub={`${data.trial_conversion?.converted || 0} converted · ${data.trial_conversion?.churned || 0} churned`}
          testid="summary-trial-conversion"
        />
        <SummaryCell
          icon={Users}
          label="Retained (W1 + W2)"
          value={data.cohort_w1_w2?.retained ?? 0}
          sub={`Cohort size ${data.cohort_w1_w2?.cohort_size || 0}`}
          testid="summary-retained"
        />
        <SummaryCell
          icon={Layers}
          label="Avg lessons / paid · week"
          value={data.depth_last_7_days?.avg_lessons_per_paid_user ?? 0}
          sub={`${data.totals?.paid_users || 0} paid teachers`}
          testid="summary-avg-lessons"
        />
        <SummaryCell
          icon={Clock}
          label="Median time to 2nd lesson"
          value={`${data.time_to_second_lesson?.median_hours ?? 0}h`}
          sub={`${data.time_to_second_lesson?.single_session_users || 0} single-session users`}
          testid="summary-time-to-second"
        />
      </div>

      {/* Trial conversion detail */}
      <details
        className="ae-card p-5 md:p-6 group"
        open
        data-testid="section-trial-conversion"
      >
        <summary className="cursor-pointer font-extrabold text-lg text-[#4a6984] tracking-tight flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Trial conversion
          <span className="ml-auto text-xs font-semibold text-[#4a6984]/60 group-open:hidden">
            (click to expand)
          </span>
        </summary>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              In trial
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {data.trial_conversion?.in_trial ?? 0}
            </p>
          </div>
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              Expired trials
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {data.trial_conversion?.expired_trials ?? 0}
            </p>
          </div>
          <div className="rounded-md bg-emerald-50 p-3 border border-emerald-200/60">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-emerald-800">
              Converted
            </p>
            <p className="font-black text-xl text-emerald-900 tabular-nums">
              {data.trial_conversion?.converted ?? 0}
            </p>
          </div>
          <div className="rounded-md bg-orange-50 p-3 border border-orange-200/60">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-orange-800">
              Churned
            </p>
            <p className="font-black text-xl text-orange-900 tabular-nums">
              {data.trial_conversion?.churned ?? 0}
            </p>
          </div>
        </div>
      </details>

      {/* W1 vs W2 cohort */}
      <details
        className="ae-card p-5 md:p-6"
        open
        data-testid="section-cohort"
      >
        <summary className="cursor-pointer font-extrabold text-lg text-[#4a6984] tracking-tight flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Week 1 vs Week 2 retention
        </summary>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-5">
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              Avg lessons · W1
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {data.cohort_w1_w2?.w1_avg_lessons ?? 0}
            </p>
          </div>
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              Avg lessons · W2
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {data.cohort_w1_w2?.w2_avg_lessons ?? 0}
            </p>
          </div>
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              Drop-off
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {fmtPct(data.cohort_w1_w2?.dropoff_pct)}
            </p>
          </div>
        </div>
        <SortableTable
          testid="cohort-table"
          columns={[
            { key: "cohort", label: "Cohort" },
            { key: "count", label: "Users" },
            { key: "hint", label: "What it means" },
          ]}
          rows={cohortRows}
        />
      </details>

      {/* Depth of use */}
      <details
        className="ae-card p-5 md:p-6"
        data-testid="section-depth"
      >
        <summary className="cursor-pointer font-extrabold text-lg text-[#4a6984] tracking-tight flex items-center gap-2">
          <Layers className="h-4 w-4" />
          Depth of use — lessons per paid user (last 7 days)
        </summary>
        <div className="mt-4">
          <SortableTable
            testid="depth-histogram-table"
            columns={[
              { key: "bucket", label: "Lessons in last 7 days" },
              { key: "users", label: "Paid users" },
            ]}
            rows={Object.entries(histogram).map(([bucket, users]) => ({
              bucket,
              users,
            }))}
          />
        </div>
      </details>

      {/* Feature penetration */}
      <details
        className="ae-card p-5 md:p-6"
        open
        data-testid="section-feature-penetration"
      >
        <summary className="cursor-pointer font-extrabold text-lg text-[#4a6984] tracking-tight flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Feature penetration — paid users
        </summary>
        <p className="mt-2 text-sm text-[#4a6984]/75">
          % of paid users who have used each feature at least once.
        </p>
        <div className="mt-4">
          <SortableTable
            testid="feature-penetration-table"
            columns={[
              { key: "feature", label: "Feature" },
              { key: "used", label: "Used" },
              { key: "total", label: "Paid users" },
              {
                key: "pct",
                label: "%",
                render: (v) => (
                  <span className="font-extrabold text-[#1c2d3d]">{fmtPct(v)}</span>
                ),
              },
            ]}
            rows={featureRows}
          />
        </div>
      </details>

      {/* Report Card Builder depth */}
      <details
        className="ae-card p-5 md:p-6"
        data-testid="section-rcb-depth"
      >
        <summary className="cursor-pointer font-extrabold text-lg text-[#4a6984] tracking-tight flex items-center gap-2">
          <Users className="h-4 w-4" />
          Report Card Builder depth
        </summary>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              Paid users who opened RCB
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {data.report_card_builder?.paid_users_opened ?? 0}
            </p>
          </div>
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              Paid users who generated a comment
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {data.report_card_builder?.paid_users_generated_comment ?? 0}
            </p>
          </div>
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              Paid users who used Live Reports
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {data.report_card_builder?.paid_users_used_live_reports ?? 0}
            </p>
          </div>
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              Avg subjects with comments
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {data.report_card_builder?.avg_subjects_with_comments ?? 0}
            </p>
          </div>
        </div>
      </details>

      {/* Time-to-second-lesson */}
      <details
        className="ae-card p-5 md:p-6"
        data-testid="section-time-to-second"
      >
        <summary className="cursor-pointer font-extrabold text-lg text-[#4a6984] tracking-tight flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Time to second lesson
        </summary>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              Median (hours)
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {data.time_to_second_lesson?.median_hours ?? 0}
            </p>
          </div>
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              Users with a 2nd lesson
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {data.time_to_second_lesson?.users_with_second_lesson ?? 0}
            </p>
          </div>
          <div className="rounded-md bg-[#f4f5f7] p-3">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4a6984]/70">
              Single-session users
            </p>
            <p className="font-black text-xl text-[#1c2d3d] tabular-nums">
              {data.time_to_second_lesson?.single_session_users ?? 0}
            </p>
          </div>
        </div>
      </details>
    </div>
  );
};

export default RetentionUsagePanel;
